<script setup lang="ts">
import { useAuthorizationStore } from '@/stores/authorization'
import { useWalletStore } from '@/stores/wallet'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const walletStore = useWalletStore()
const authzStore = useAuthorizationStore()

const SESSION_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes
const WARNING_BEFORE_MS = 5 * 60 * 1000 // 5 minutes before expiry
const CHECK_INTERVAL_MS = 30 * 1000 // Check every 30 seconds

const showWarning = ref(false)
const showExpired = ref(false)
const remainingMinutes = ref(0)
let checkInterval: ReturnType<typeof setInterval> | null = null
let activityTimeout: ReturnType<typeof setTimeout> | null = null
let handlingForcedExpiry = false

function resetActivityTimer() {
  if (!walletStore.isConnected) return
  walletStore.refreshSession(SESSION_TIMEOUT_MS)
  showWarning.value = false
}

function checkSession() {
  if (!walletStore.isConnected || !walletStore.sessionExpiry) return

  const now = Date.now()
  const remaining = walletStore.sessionExpiry - now

  if (remaining <= 0) {
    // Session expired
    handleExpired()
  } else if (remaining <= WARNING_BEFORE_MS) {
    // Show warning
    remainingMinutes.value = Math.ceil(remaining / 60000)
    showWarning.value = true
  } else {
    showWarning.value = false
  }
}

function handleExpired() {
  handleForcedExpired()
}

function handleExtendSession() {
  resetActivityTimer()
}

function handleLogout() {
  showWarning.value = false
  walletStore.disconnect()
  authzStore.clearRole()
  router.push({ name: 'login', query: { signout: '1' } })
}

async function handleForcedExpired() {
  if (handlingForcedExpiry) return
  handlingForcedExpiry = true
  showWarning.value = false
  showExpired.value = true

  try {
    await walletStore.disconnect()
  } finally {
    authzStore.clearRole()
    handlingForcedExpiry = false
  }
}

function handleExpiredOk() {
  showExpired.value = false
  router.replace({ name: 'login', query: { signout: '1' } })
}

// Track user activity to refresh session
function onUserActivity() {
  if (activityTimeout) clearTimeout(activityTimeout)
  activityTimeout = setTimeout(() => {
    if (walletStore.isConnected && !showWarning.value) {
      walletStore.refreshSession(SESSION_TIMEOUT_MS)
    }
  }, 1000) // Debounce activity events
}

onMounted(() => {
  if (route.query.expired === '1') {
    handleForcedExpired()
  }

  // Initialize session expiry if not set
  if (walletStore.isConnected && !walletStore.sessionExpiry) {
    walletStore.refreshSession(SESSION_TIMEOUT_MS)
  }

  // Start periodic session check
  checkInterval = setInterval(checkSession, CHECK_INTERVAL_MS)

  // Listen for user activity
  window.addEventListener('click', onUserActivity)
  window.addEventListener('keydown', onUserActivity)
  window.addEventListener('mousemove', onUserActivity)
  window.addEventListener('scroll', onUserActivity)
  window.addEventListener('nomadian:session-expired', handleForcedExpired)
})

onUnmounted(() => {
  if (checkInterval) clearInterval(checkInterval)
  if (activityTimeout) clearTimeout(activityTimeout)
  window.removeEventListener('click', onUserActivity)
  window.removeEventListener('keydown', onUserActivity)
  window.removeEventListener('mousemove', onUserActivity)
  window.removeEventListener('scroll', onUserActivity)
  window.removeEventListener('nomadian:session-expired', handleForcedExpired)
})

// Watch for disconnect to clean up
watch(() => walletStore.isConnected, (connected) => {
  if (!connected) {
    showWarning.value = false
  }
})

watch(() => route.query.expired, (expired) => {
  if (expired === '1') {
    handleForcedExpired()
  }
})
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="showWarning || showExpired"
        class="fixed inset-0 z-[60] flex items-center justify-center"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" />

        <!-- Modal -->
        <div class="relative mx-4 w-full max-w-sm rounded-2xl border border-border bg-bg-secondary p-6 shadow-xl">
          <!-- Warning icon -->
          <div class="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-yellow-500/15">
            <svg class="h-7 w-7 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>

          <h3 class="text-center text-lg font-semibold text-text-primary">
            <template v-if="showExpired">{{ t('session.expiredTitle') }}</template>
            <template v-else>{{ t('session.timeoutTitle') }}</template>
          </h3>
          <p v-if="showExpired" class="mt-2 text-center text-sm text-text-secondary">
            {{ t('session.expiredMessage') }}
          </p>
          <p v-else class="mt-2 text-center text-sm text-text-secondary">
            {{ t('session.timeoutMessage', { minutes: remainingMinutes }) }}
          </p>

          <!-- Actions -->
          <div v-if="showExpired" class="mt-6">
            <button
              class="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary px-4 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-brand-primary"
              @click="handleExpiredOk"
            >
              {{ t('common.ok') }}
            </button>
          </div>
          <div v-else class="mt-6 space-y-3">
            <button
              class="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary px-4 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-brand-primary"
              @click="handleExtendSession"
            >
              {{ t('session.extend') }}
            </button>
            <button
              class="flex w-full items-center justify-center gap-2 rounded-xl border border-border px-4 py-2.5 text-sm font-medium text-text-secondary transition-all duration-200 hover:bg-bg-hover hover:text-text-primary"
              @click="handleLogout"
            >
              {{ t('session.logout') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
